import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MessageSquareText, Loader2, CheckCircle2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      setForgotSent(true);
    } catch (_e) {
      setForgotSent(true); // always show success to prevent enumeration
    } finally {
      setForgotSubmitting(false);
    }
  };

  useEffect(() => {
    if (user) nav("/inbox", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
    else nav("/inbox", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      <div className="hidden md:flex md:w-1/2 bg-black text-white p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="South Texas Strutters" className="w-14 h-14 object-contain bg-white rounded-lg p-1" />
          <div className="leading-tight">
            <div className="text-xl font-bold tracking-tight">STS Connect</div>
            <div className="text-xs text-slate-400 tracking-wide uppercase">South Texas Strutters</div>
          </div>
        </div>
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            Where dancing<br />
            <span className="text-[#D9232E]">is fun.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Text your customers, families, and classes from one clean inbox. Group texts. Individual replies. Scheduled sends.
          </p>
        </div>
        <div className="text-xs text-slate-500 tracking-wider uppercase">Connected to Twilio &middot; Jackrabbit Class</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="South Texas Strutters" className="w-10 h-10 object-contain" />
            <span className="text-xl font-bold">STS Connect</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Welcome back. Access your inbox.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.com"
              required
              data-testid="login-email-input"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              className="h-11"
            />
          </div>

          {error && (
            <div className="text-sm text-[#D9232E] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-[#D9232E] hover:bg-[#B91C25] text-white font-semibold"
            data-testid="login-submit-button"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
          <div className="text-center">
            <button
              type="button"
              onClick={() => { setForgotOpen(true); setForgotEmail(email); setForgotSent(false); }}
              className="text-xs text-[#D9232E] hover:underline"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">
            Protected by JWT + bcrypt. Contact your admin to add users.
          </p>
        </form>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(v) => { setForgotOpen(v); if (!v) { setForgotSent(false); setForgotEmail(""); } }}>
        <DialogContent data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>Reset your password</DialogTitle>
            <DialogDescription>
              Enter your email and we'll send you a link to set a new password. The link expires in 1 hour.
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-200 p-4" data-testid="forgot-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-emerald-900">Check your email</div>
                <div className="text-emerald-700 mt-1">If <span className="font-medium">{forgotEmail}</span> is on the account list, a reset link is on the way. Check spam too, just in case.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@business.com"
                  required
                  data-testid="forgot-email-input"
                  className="h-11"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={forgotSubmitting || !forgotEmail.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="forgot-submit-button">
                  {forgotSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
